import {
    ObjectAnalyticsObjectProperty,
    resolveObjectPropertyField,
} from '@/object-analytics/aggregations/object-analytics-object-property.helper';

export const FUNNEL_PRIMARY_KEY_TYPE = {
    VISITOR: 'visitor',
    ACCOUNT: 'account',
    OBJECT: 'object',
} as const;

export type FunnelPrimaryKeyType = (typeof FUNNEL_PRIMARY_KEY_TYPE)[keyof typeof FUNNEL_PRIMARY_KEY_TYPE];

export type FunnelPrimaryKey =
    | { type: 'visitor' }
    | { type: 'account' }
    | { type: 'object'; property: ObjectAnalyticsObjectProperty };

export interface ResolvedFunnelPrimaryKey {
    type: FunnelPrimaryKeyType;
    // Source expression for the primary key value in raw events
    // (`visitorId`, `accountId`, `properties.reportId`, ...).
    field: string;
    // Top-level pipeline field the funnel op groups/sorts/keys on. Equal to
    // `field` for non-nested sources; for nested sources (e.g.
    // `properties.X`) it's a synthetic name that an upstream
    // `evalExpression` projects the nested value into — the BE
    // pathAggregator can't accept a nested path as an entity key
    // (it materializes to RawJsonRow and panics).
    entityField: string;
}

// Synthetic top-level field name used when the primary key source is a
// nested path; chosen to match the downstream `primaryKeyValue` select
// alias so the pipeline reads the same field name end-to-end.
const NESTED_ENTITY_FIELD = 'primaryKeyValue';

// The funnel's unit of analysis is the aggregation group key. Absent config
// means visitor — keep that the single default so old and new saved funnels
// share one code path with no migration.
export function resolveFunnelPrimaryKey (config?: { primaryKey?: FunnelPrimaryKey } | null): ResolvedFunnelPrimaryKey {
    const primaryKey = config?.primaryKey;

    if (!primaryKey || primaryKey.type === FUNNEL_PRIMARY_KEY_TYPE.VISITOR) {
        return { type: FUNNEL_PRIMARY_KEY_TYPE.VISITOR, field: 'visitorId', entityField: 'visitorId' };
    }

    if (primaryKey.type === FUNNEL_PRIMARY_KEY_TYPE.ACCOUNT) {
        return { type: FUNNEL_PRIMARY_KEY_TYPE.ACCOUNT, field: 'accountId', entityField: 'accountId' };
    }

    const field = resolveObjectPropertyField(primaryKey.property);
    const entityField = field.includes('.') ? NESTED_ENTITY_FIELD : field;

    return { type: FUNNEL_PRIMARY_KEY_TYPE.OBJECT, field, entityField };
}

export function isVisitorPrimaryKey (config?: { primaryKey?: FunnelPrimaryKey } | null): boolean {
    return resolveFunnelPrimaryKey(config).type === FUNNEL_PRIMARY_KEY_TYPE.VISITOR;
}

export function isAccountPrimaryKey (config?: { primaryKey?: FunnelPrimaryKey } | null): boolean {
    return resolveFunnelPrimaryKey(config).type === FUNNEL_PRIMARY_KEY_TYPE.ACCOUNT;
}
